import { useCallback, useMemo, useRef, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { FornecedorDetalhe } from "../../domain/models/fornecedor_detalhe";
import { FornecedorBanco } from "../../domain/models/fornecedor_banco";
import { FornecedorAvaliacao } from "../../domain/models/fornecedor_avaliacao";
import { FornecedorDocumento } from "../../domain/models/fornecedor_documento";
import { FornecedorForm, FornecedorSchema } from "../schemas/Fornecedor_cadastro/fornecedor";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";
import { CentroCustoDropDown } from "../../../../common/domain/models/centro_custo_dropdown";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { FornecedorAvaliacaoItem } from "../../domain/models/fornecedor_avaliacao_item";
import { FornecedorDocumentoForm } from "../schemas/Fornecedor_cadastro/fornecedor_documento";
import { FornecedorBancoForm } from "../schemas/Fornecedor_cadastro/fornecedor_banco";
import { FornecedorAvaliacaoEditarForm } from "../schemas/Fornecedor_cadastro/fornecedor_avaliacao_editar";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";

import dayjs from "dayjs";

/**
 * useFornecedoresCadastroViewModel
 */

export const useFornecedoresCadastroViewModel = () => {

  const {
    rh: {
      getCepUseCase,
    },
    compras: {
      getFornecedorByIdUseCase,
      getBancosByIdFornecedorUseCase,
      getAvaliacoesByIdFornecedorUseCase,
      getDocumentosByIdFornecedorUseCase,
      getFornecedorDocumentoFileContentUseCase,
      toggleFornecedorDesqualificadoUseCase,
      getAllFornecedorQualificacaoUseCase,
      getFornecedorAvaliacaoItensByIdUseCase,
      deleteFornecedorAvaliacaoUseCase,
      createFornecedorBancoUseCase,
      updateFornecedorBancoUseCase,
      deleteFornecedorBancoUseCase,
      updateFornecedorAvaliacaoUseCase,
      createFornecedorDocumentoUseCase,
      updateFornecedorDocumentoUseCase,
      deleteFornecedorDocumentoUseCase,
      createFornecedorUseCase,
      updateFornecedorUseCase,
      deleteFornecedorUseCase,
    },
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
      getFornecedorDadosByCnpjUseCase,
    }
  } = useDependencyInjection();

  const {
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
  } = useDropDownLoader();

  const enableFetchEndereco = useRef(true);

  const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageSaving, setCustomLoading } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const location = useLocation();

  const [idFornecedor, setIdFornecedor] = useState<number>();
  const [fornecedor, setFornecedor] = useState<FornecedorDetalhe>();

  const [bancos, setBancos] = useState<FornecedorBanco[]>();
  const [avaliacoes, setAvaliacoes] = useState<FornecedorAvaliacao[]>();
  const [documentos, setDocumentos] = useState<FornecedorDocumento[]>();

  const [selectedBanco, setSelectedBanco] = useState<FornecedorBanco>();
  const [selectedAvaliacao, setSelectedAvaliacao] = useState<FornecedorAvaliacao>();
  const [selectedDocumento, setSelectedDocumento] = useState<FornecedorDocumento>();

  const [fornecedorAvaliacaoItems, setFornecedorAvaliacaoItems] = useState<FornecedorAvaliacaoItem[]>();

  const [centroCustoDropdown, setCentroCustoDropdown] = useState<CentroCustoDropDown[]>();
  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [mediaGeral, setMediaGeral] = useState<number>();

  const [bancoModal, setBancoModal] = useState(false);
  const [avaliacaoModal, setAvaliacaoModal] = useState(false);
  const [documentoModal, setDocumentoModal] = useState(false);

  const [fornecedorDeleteModal, setFornecedorDeleteModal] = useState(false);
  const [bancoDeleteModal, setBancoDeleteModal] = useState(false);
  const [avaliacaoDeleteModal, setAvaliacaoDeleteModal] = useState(false);
  const [documentoDeleteModal, setDocumentoDeleteModal] = useState(false);

  const hookForm = useForm<FornecedorForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(FornecedorSchema),
  });

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: message => pushErrorNotification('Erro', message),
  });

  const [qualificacoes] = hookForm.watch(['qualificacoes']);

  const buttonOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: 'Novo fornecedor',
      },
      {
        id: 2,
        label: 'Excluir',
      },
    ];
  }, [
  ]);

  const filteredFuncionariosDropdown = useMemo(() => {
    return funcionariosDropdown
      ? funcionariosDropdown.filter(x => {
        if (selectedAvaliacao) {
          if (selectedAvaliacao.idFuncionarioAvaliador === x.idFuncionario) {
            return true;
          }
        }
        return x.canBeSelected === true;
      })
      : null;
  }, [
    selectedAvaliacao,
    funcionariosDropdown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadFornecedor = useCallback(async (id?: number) => {
    try {
      enableFetchEndereco.current = false;

      const filteredIdFornecedor = evaluateParamValue(id, idFornecedor);

      const result = await getFornecedorByIdUseCase
        .getFornecedorById(filteredIdFornecedor);

      hookForm.reset();
      hookForm.setValue('ativo', result.ativo ?? false);
      hookForm.setValue('avaliar', result.avaliar ?? false);
      hookForm.setValue('fornProduto', result.fornProduto ?? false);
      hookForm.setValue('fornServico', result.fornServico ?? false);
      hookForm.setValue('cnpj', result.cnpj ?? '');
      hookForm.setValue('nomeFantasia', result.nomeFantasia ?? '');
      hookForm.setValue('familia', result.familia ?? '');
      hookForm.setValue('idCentroCusto', result.idCentroCusto);
      hookForm.setValue('razaoSocial', result.razaoSocial ?? '');
      hookForm.setValue('cpf', result.cpf ?? '');
      hookForm.setValue('contato', result.contato ?? '');
      hookForm.setValue('email', result.email ?? '');
      hookForm.setValue('contato1', result.contato1 ?? '');
      hookForm.setValue('email1', result.email1 ?? '');
      hookForm.setValue('inscricaoMunicipal', result.incricaoMunicipal ?? '');
      hookForm.setValue('telefoneFixo', result.telefoneFixo ?? '');
      hookForm.setValue('inscricaoEstadual', result.incricaoEstadual ?? '');
      hookForm.setValue('telefoneCelular', result.telefoneCelular ?? '');
      hookForm.setValue('atividade', result.atividade ?? '');
      hookForm.setValue('dataCadastro', result.dataCadastro ? dayjs(result.dataCadastro).toDate() : new Date(Date.now()));
      hookForm.setValue('avaliacaoFinanceira', result.avaliacaoFinanceira ?? '');
      hookForm.setValue('dataValidade', result.dataValidade ? dayjs(result.dataValidade).toDate() : null);
      hookForm.setValue('cep', result.cep ? result.cep.replaceAll('.', '').replaceAll('-', '') : '');
      hookForm.setValue('cidade', result.cidade ?? '');
      hookForm.setValue('rua', result.rua ?? '');
      hookForm.setValue('numero', result.numero ?? '');
      hookForm.setValue('apartamento', result.apartamento ?? '');
      hookForm.setValue('bairro', result.bairro ?? '');
      hookForm.setValue('uf', result.uf ?? '');
      hookForm.setValue('pais', result.pais ?? '');
      hookForm.setValue('obsFornecedor', result.obsFornecedor ?? '');
      hookForm.setValue('descQualificacoes', result.descQualificacoes ?? '');

      setFornecedor(result);
    } catch (e: unknown) {
      setFornecedor(undefined);
      throw new Error('Falha ao buscar fornecedor!');
    } finally {
      requestAnimationFrame(() => {
        enableFetchEndereco.current = true;
      });
    }
  }, [
    idFornecedor,
    hookForm,
    getFornecedorByIdUseCase,
  ]);

  const loadBancos = useCallback(async (id?: number) => {
    try {
      const filteredIdFornecedor = id ?? idFornecedor;
      const results = await getBancosByIdFornecedorUseCase
        .getBancosByIdFornecedor(filteredIdFornecedor);
      setBancos(results);
    } catch (e: unknown) {
      setBancos(undefined);
      throw new Error('Falha ao buscar bancos do fornecedor!');
    }
  }, [
    idFornecedor,
    getBancosByIdFornecedorUseCase,
  ]);

  const loadAvaliacoes = useCallback(async (id?: number) => {
    try {
      const filteredIdFornecedor = id ?? idFornecedor;

      const results = await getAvaliacoesByIdFornecedorUseCase
        .getAvaliacoesByIdFornecedor(filteredIdFornecedor);

      if (results.length > 0) {
        setMediaGeral(results[0].mediaGeral);
      } else {
        setMediaGeral(undefined);
      }
      setAvaliacoes(results);
    } catch (e: unknown) {
      setAvaliacoes(undefined);
      throw new Error('Falha ao buscar avaliações do fornecedor!');
    }
  }, [
    idFornecedor,
    getAvaliacoesByIdFornecedorUseCase,
  ]);

  const loadDocumentos = useCallback(async (id?: number) => {
    try {
      const filteredIdFornecedor = id ?? idFornecedor;
      const results = await getDocumentosByIdFornecedorUseCase
        .getDocumentosByIdFornecedor(filteredIdFornecedor);
      setDocumentos(results);
    } catch (e: unknown) {
      setDocumentos(undefined);
      throw new Error('Falha ao buscar documentos do fornecedor!');
    }
  }, [
    idFornecedor,
    getDocumentosByIdFornecedorUseCase,
  ]);

  const loadQualificacoes = useCallback(async (id?: number | null) => {
    try {
      const filteredIdFornecedor = evaluateParamValue(id, idFornecedor);

      const results = await getAllFornecedorQualificacaoUseCase
        .getAllFornecedorQualificacao(filteredIdFornecedor);

      hookForm.setValue('qualificacoes', results.map(x => ({
        idQualificacao: x.idQualificacao,
        descricao: x.descricao ?? '',
        qualificado: x.qualificado ?? false,
        possuiObservacao: x.possuiObservacao,
        observacao: x.observacao ?? '',
        grupo: x.grupo ?? '',
        ordem: x.ordem ?? 0,
      })));
    } catch (e: unknown) {
      throw new Error('Falha ao buscar qualificações do fornecedor!');
    }
  }, [
    idFornecedor,
    hookForm,
    getAllFornecedorQualificacaoUseCase,
  ]);


  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id');
      const id = locationId && locationId.length > 0 ? Number(locationId) : undefined;

      setIdFornecedor(id);

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      if (id) {
        await loadFornecedor(id);

        await loadBancos(id);

        await loadAvaliacoes(id);

        await loadDocumentos(id);
      } else {
        hookForm.reset({
          ativo: true,
          avaliar: true,
          fornProduto: true,
        });
      }
      await loadQualificacoes(id);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadCentroCustoDropDown(dt => setCentroCustoDropdown(dt));

      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });

      if (idFornecedor) {
        await loadFornecedor();

        await loadBancos();

        await loadAvaliacoes();

        await loadDocumentos();
      }
      await loadQualificacoes();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!')[0]);
    } finally {
      setLoading(false);
    }
  }, [
    idFornecedor,
    loadCentroCustoDropDown,
    loadFuncionariosDropDown,
    loadQualificacoes,
    loadFornecedor,
    loadBancos,
    loadAvaliacoes,
    loadDocumentos,
    pushErrorNotification,
  ]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: 'Fornecedores_lista' });
  }, [
    backOrNavigate,
  ]);

  const handleSubmit = useCallback(async (form: FornecedorForm) => {
    try {
      setSaving(true);
      setPageSaving(true);

      if (idFornecedor) {
        await updateFornecedorUseCase.updateFornecedor(
          idFornecedor,
          form.idCentroCusto,
          form.dataCadastro,
          form.razaoSocial,
          form.nomeFantasia,
          form.cnpj,
          form.cpf,
          form.inscricaoEstadual,
          form.inscricaoMunicipal,
          form.atividade,
          form.obsFornecedor,
          form.contato,
          form.telefoneFixo,
          form.telefoneCelular,
          form.email,
          form.contato1,
          form.email1,
          form.descQualificacoes,
          form.fornProduto,
          form.fornServico,
          form.familia,
          form.ativo,
          form.avaliar,
          form.avaliacaoFinanceira,
          form.dataValidade,
          form.rua,
          form.numero,
          form.apartamento,
          form.cep,
          form.uf,
          form.cidade,
          form.bairro,
          form.pais,
          form.qualificacoes,
        );
      } else {
        const newIdFornecedor = await createFornecedorUseCase.createFornecedor(
          form.idCentroCusto,
          form.dataCadastro,
          form.razaoSocial,
          form.nomeFantasia,
          form.cnpj,
          form.cpf,
          form.inscricaoEstadual,
          form.inscricaoMunicipal,
          form.atividade,
          form.obsFornecedor,
          form.contato,
          form.telefoneFixo,
          form.telefoneCelular,
          form.email,
          form.contato1,
          form.email1,
          form.descQualificacoes,
          form.fornProduto,
          form.fornServico,
          form.familia,
          form.ativo,
          form.avaliar,
          form.avaliacaoFinanceira,
          form.dataValidade,
          form.rua,
          form.numero,
          form.apartamento,
          form.cep,
          form.uf,
          form.cidade,
          form.bairro,
          form.pais,
          form.qualificacoes,
        );

        await loadFornecedor(newIdFornecedor);

        await loadBancos(newIdFornecedor);

        await loadAvaliacoes(newIdFornecedor);

        await loadDocumentos(newIdFornecedor);

        await loadQualificacoes(newIdFornecedor);

        setIdFornecedor(newIdFornecedor);

        const params = new URLSearchParams(window.location.search);
        params.set("id", newIdFornecedor.toString());

        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao salvar fornecedor!'));
    } finally {
      setSaving(false);
      setPageSaving(false);
    }
  }, [
    idFornecedor,
    createFornecedorUseCase,
    updateFornecedorUseCase,
    loadFornecedor,
    loadBancos,
    loadAvaliacoes,
    loadDocumentos,
    loadQualificacoes,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleFornecedorDeleteSubmit = useCallback(async () => {
    if (idFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);
        await deleteFornecedorUseCase.deleteFornecedor(idFornecedor);
        backOrNavigate(-1, { alternativeRoute: 'Fornecedores_lista' });
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir fornecedor!'))
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    deleteFornecedorUseCase,
    backOrNavigate,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleBancoSubmit = useCallback(async (form: FornecedorBancoForm) => {
    if (idFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedBanco) {
          await updateFornecedorBancoUseCase.updateFornecedorBanco(
            idFornecedor,
            selectedBanco.idBanco,
            form.tipoConta,
            form.banco,
            form.agencia,
            form.conta,
            form.contaDigito,
            form.operacao,
            form.favorecido,
            form.cpfCnpj,
            form.pix,
          );
        } else {
          await createFornecedorBancoUseCase.createFornecedorBanco(
            idFornecedor,
            form.tipoConta,
            form.banco,
            form.agencia,
            form.conta,
            form.contaDigito,
            form.operacao,
            form.favorecido,
            form.cpfCnpj,
            form.pix,
          );
        }

        await loadBancos();

        setBancoModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar banco!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedBanco,
    updateFornecedorBancoUseCase,
    createFornecedorBancoUseCase,
    loadBancos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleAvaliacaoSubmit = useCallback(async (form: FornecedorAvaliacaoEditarForm) => {
    if (idFornecedor && selectedAvaliacao) {
      try {
        setSaving(true);
        setPageSaving(true);

        await updateFornecedorAvaliacaoUseCase.updateFornecedorAvaliacao(
          idFornecedor,
          selectedAvaliacao.idAvaliacaoFornecedor,
          form.avaliadoPor,
          form.data,
          form.observacao,
          form.items,
        );

        await loadAvaliacoes();
        await loadFornecedor();
        await loadQualificacoes();

        setAvaliacaoModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar avaliação!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedAvaliacao,
    updateFornecedorAvaliacaoUseCase,
    loadAvaliacoes,
    loadFornecedor,
    loadQualificacoes,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleDocumentoSubmit = useCallback(async (form: FornecedorDocumentoForm) => {
    if (idFornecedor) {
      try {
        setSaving(true);
        setPageSaving(true);

        if (selectedDocumento) {
          await updateFornecedorDocumentoUseCase.updateFornecedorDocumento(
            idFornecedor,
            selectedDocumento.idFornecedorArquivo,
            form.status,
            form.nomeDocumento,
            form.dataVencimento,
            form.file,
            form.limparArquivo,
          );
        } else {
          await createFornecedorDocumentoUseCase.createFornecedorDocumento(
            idFornecedor,
            form.status,
            form.nomeDocumento,
            form.dataVencimento,
            form.file,
          );
        }

        await loadDocumentos();

        setDocumentoModal(false);

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao salvar documento!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedDocumento,
    createFornecedorDocumentoUseCase,
    updateFornecedorDocumentoUseCase,
    loadDocumentos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleBancoDeleteSubmit = useCallback(async () => {
    if (idFornecedor && selectedBanco) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteFornecedorBancoUseCase.deleteFornecedorBanco(
          idFornecedor,
          selectedBanco.idBanco,
        );

        await loadBancos();

        setBancoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir banco!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedBanco,
    deleteFornecedorBancoUseCase,
    loadBancos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleAvaliacaoDeleteSubmit = useCallback(async () => {
    if (idFornecedor && selectedAvaliacao) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteFornecedorAvaliacaoUseCase.deleteFornecedorAvaliacao(
          idFornecedor,
          selectedAvaliacao.idAvaliacaoFornecedor,
        );

        await loadAvaliacoes();
        await loadFornecedor();
        await loadQualificacoes();

        setAvaliacaoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir banco!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedAvaliacao,
    deleteFornecedorAvaliacaoUseCase,
    loadAvaliacoes,
    loadFornecedor,
    loadQualificacoes,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleDocumentoDeleteSubmit = useCallback(async () => {
    if (idFornecedor && selectedDocumento) {
      try {
        setSaving(true);
        setPageSaving(true);

        await deleteFornecedorDocumentoUseCase.deleteFornecedorDocumento(
          idFornecedor,
          selectedDocumento.idFornecedorArquivo,
        );

        await loadDocumentos();

        setDocumentoDeleteModal(false);

        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao excluir banco!'));
      } finally {
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    selectedDocumento,
    deleteFornecedorDocumentoUseCase,
    loadDocumentos,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleBancoModalOpenState = useCallback((idBanco: number | null, openState: boolean) => {
    if (openState) {
      if (idBanco && bancos) {
        setSelectedBanco(bancos.find(x => x.idBanco === idBanco));
      } else {
        setSelectedBanco(undefined);
      }
    }
    setBancoModal(openState);
  }, [
    bancos,
  ]);

  const handleAvaliacaoModalOpenState = useCallback(async (idAvaliacaoFornecedor: number | null, openState: boolean) => {
    if (openState) {
      if (idAvaliacaoFornecedor && avaliacoes) {
        try {
          setLoading(true);
          setSelectedAvaliacao(avaliacoes.find(x => x.idAvaliacaoFornecedor === idAvaliacaoFornecedor));
          const response = await getFornecedorAvaliacaoItensByIdUseCase
            .getFornecedorAvaliacaoItensById(idAvaliacaoFornecedor);
          setFornecedorAvaliacaoItems(response);
          setAvaliacaoModal(openState);
        } catch (e: unknown) {
          setFornecedorAvaliacaoItems(undefined);
          pushErrorNotification('Erro', processError(e, 'Falha ao buscar items de avaliação!'));
        } finally {
          setLoading(false);
        }
      } else {
        setAvaliacaoModal(openState);
      }
    } else {
      setAvaliacaoModal(openState);
    }
  }, [
    avaliacoes,
    getFornecedorAvaliacaoItensByIdUseCase,
    pushErrorNotification,
  ]);

  const handleDocumentoModalOpenState = useCallback((idFornecedorArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idFornecedorArquivo && documentos) {
        setSelectedDocumento(documentos.find(x => x.idFornecedorArquivo === idFornecedorArquivo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setDocumentoModal(openState);
  }, [
    documentos,
  ]);

  const handleDocumentoDownload = useCallback(async (idFornecedorArquivo: number, nomeArquivo: string) => {
    if (idFornecedor) {
      try {
        setCustomLoading(true);
        await getFornecedorDocumentoFileContentUseCase
          .getFornecedorDocumentoFileContent(idFornecedor, idFornecedorArquivo, nomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados do arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    }
  }, [
    idFornecedor,
    getFornecedorDocumentoFileContentUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleFornecedorDeleteModalOpenState = useCallback((openState: boolean) => {
    setFornecedorDeleteModal(openState);
  }, [
  ]);

  const handleBancoDeleteModalOpenState = useCallback((idBanco: number | null, openState: boolean) => {
    if (openState) {
      if (idBanco && bancos) {
        setSelectedBanco(bancos.find(x => x.idBanco === idBanco));
      } else {
        setSelectedBanco(undefined);
      }
    }
    setBancoDeleteModal(openState);
  }, [
    bancos,
  ]);

  const handleAvaliacaoDeleteModalOpenState = useCallback((idAvaliacaoFornecedor: number | null, openState: boolean) => {
    if (openState) {
      if (idAvaliacaoFornecedor && avaliacoes) {
        setSelectedAvaliacao(avaliacoes.find(x => x.idAvaliacaoFornecedor === idAvaliacaoFornecedor));
      } else {
        setSelectedAvaliacao(undefined);
      }
    }
    setAvaliacaoDeleteModal(openState);
  }, [
    avaliacoes,
  ]);

  const handleDocumentoDeleteModalOpenState = useCallback((idFornecedorArquivo: number | null, openState: boolean) => {
    if (openState) {
      if (idFornecedorArquivo && documentos) {
        setSelectedDocumento(documentos.find(x => x.idFornecedorArquivo === idFornecedorArquivo));
      } else {
        setSelectedDocumento(undefined);
      }
    }
    setDocumentoDeleteModal(openState);
  }, [
    documentos,
  ]);

  const handlePopperActions = useCallback(async (option: number) => {
    switch (option) {
      case 1:
        hookForm.reset({
          ativo: true,
          avaliar: true,
          fornProduto: true,
          cpf: '',
          cnpj: '',
        });

        setIdFornecedor(undefined);
        setFornecedor(undefined);

        setBancos(undefined);
        setSelectedBanco(undefined);

        setAvaliacoes(undefined);
        setSelectedAvaliacao(undefined);

        setDocumentos(undefined);
        setSelectedDocumento(undefined);

        await loadQualificacoes(null);

        const newUrl = `${window.location.pathname}`;
        window.history.replaceState({}, "", newUrl);

        break;
      case 2:
        handleFornecedorDeleteModalOpenState(true);
        break;
    }
  }, [
    hookForm,
    loadQualificacoes,
    handleFornecedorDeleteModalOpenState,
  ]);

  const handleToggleDesqualificado = useCallback(async () => {
    if (idFornecedor) {
      try {
        setLoading(true);
        setSaving(true);
        setPageSaving(true);

        await toggleFornecedorDesqualificadoUseCase
          .toggleFornecedorDesqualificado(idFornecedor);

        await loadAvaliacoes();
        await loadFornecedor();
        await loadQualificacoes();

        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados do arquivo!'));
      } finally {
        setLoading(false);
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [
    idFornecedor,
    toggleFornecedorDesqualificadoUseCase,
    loadAvaliacoes,
    loadFornecedor,
    loadQualificacoes,
    pushErrorNotification,
    pushInfoNotification,
    setPageSaving,
  ]);

  const handleToggleQualificacao = useCallback((idQualificacao: number, value: boolean) => {
    const prevState = hookForm.getValues().qualificacoes;
    const index = prevState.findIndex(x => x.idQualificacao === idQualificacao);

    if (index >= 0) {
      prevState[index].qualificado = value;
    }

    hookForm.setValue('qualificacoes', [...prevState]);
  }, [
    hookForm,
  ]);

  const handleObservacaoChange = useCallback((idQualificacao: number, value: string) => {
    const prevState = hookForm.getValues().qualificacoes;
    const index = prevState.findIndex(x => x.idQualificacao === idQualificacao);

    if (index >= 0) {
      prevState[index].observacao = value;
    }

    hookForm.setValue('qualificacoes', [...prevState]);
  }, [
    hookForm,
  ]);

  const handleCepChange = useCallback(async (cep: string) => {
    try {
      if (enableFetchEndereco.current === true) {
        if (typeof cep === "string") {
          const normalizedCep = cep.trim();
          const requiredSize = 'xxxxxxxx';

          if (normalizedCep.length === requiredSize.length) {
            const cepInfo = await getCepUseCase.getCep(cep);
            if (cepInfo) {
              hookForm.setValue('rua', cepInfo.logradouro.toUpperCase());
              hookForm.setValue('bairro', cepInfo.bairro.toUpperCase());
              hookForm.setValue('cidade', cepInfo.cidade.toUpperCase());
              hookForm.setValue('uf', cepInfo.uf.toUpperCase());
              hookForm.setValue('pais', cepInfo.pais.toUpperCase());
            }
          } else if (normalizedCep.length === 0) {
            hookForm.setValue('rua', '');
            hookForm.setValue('bairro', '');
            hookForm.setValue('cidade', '');
            hookForm.setValue('uf', '');
            hookForm.setValue('pais', '');
          }
        }
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao carregar CEP!"));
    }
  }, [
    hookForm,
    getCepUseCase,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback((idTabela: number | null, tipoTabela: LogTipoTabela | null) => {
    if (idTabela && tipoTabela) {
      changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
    } else {
      if (idFornecedor) {
        changeHistory.handleOpenChangeHistory(idFornecedor, LogTipoTabela.FORNECEDOR, true);
      }
    }
  }, [
    idFornecedor,
    changeHistory,
  ]);

  const handleGetFornecedorDadosByCnpj = useCallback(async (cnpj: string) => {
    try {
      const result = await getFornecedorDadosByCnpjUseCase.getFornecedorDadosByCnpj(cnpj);
      // hookForm.reset();
      hookForm.setValue('cnpj', result.cnpj ?? '');
      hookForm.setValue('nomeFantasia', result.nomeFantasia ?? '');
      hookForm.setValue('razaoSocial', result.razaoSocial ?? '');
      hookForm.setValue('email', result.email ?? '');
      hookForm.setValue('telefoneFixo', result.telefone ?? '');
      hookForm.setValue('cep', result.cep ? result.cep.replaceAll('.', '').replaceAll('-', '') : '');

      hookForm.setValue('cidade', result.cidade ?? '');
      hookForm.setValue('rua', result.rua ?? '');
      hookForm.setValue('numero', result.numero ?? '');
      hookForm.setValue('apartamento', result.apartamento ?? '');
      hookForm.setValue('bairro', result.bairro ?? '');
      hookForm.setValue('uf', result.uf ?? '');
      hookForm.setValue('pais', result.pais ?? '');
      pushInfoNotification('Sucesso!', 'Dados do CNPJ carregados com sucesso!');
    } catch (e: unknown) {
      pushWarnNotification('Atenção!', 'Dados do CNPJ não encontrados!');
    }
  }, [
    getFornecedorDadosByCnpjUseCase,
    hookForm,
    pushWarnNotification,
    pushInfoNotification,
  ]);


  return {
    idFornecedor,
    fornecedor: fornecedor ?? null,
    bancos: bancos ?? [],
    avaliacoes: avaliacoes ?? [],
    documentos: documentos ?? [],
    qualificacoes: qualificacoes ?? [],
    selectedBanco: selectedBanco ?? null,
    selectedAvaliacao: selectedAvaliacao ?? null,
    selectedDocumento: selectedDocumento ?? null,
    fornecedorAvaliacaoItems: fornecedorAvaliacaoItems ?? [],
    centroCustoDropdown: centroCustoDropdown ?? [],
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    loading,
    saving,
    errorMessage,
    mediaGeral: mediaGeral ?? null,
    bancoModal,
    avaliacaoModal,
    documentoModal,
    fornecedorDeleteModal,
    bancoDeleteModal,
    avaliacaoDeleteModal,
    documentoDeleteModal,
    hookForm,
    changeHistory,
    buttonOptions,

    handleReload,
    handleBack,
    handleGetFornecedorDadosByCnpj,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleFornecedorDeleteSubmit,
    handleBancoSubmit,
    handleAvaliacaoSubmit,
    handleDocumentoSubmit,
    handleBancoDeleteSubmit,
    handleAvaliacaoDeleteSubmit,
    handleDocumentoDeleteSubmit,
    handleBancoModalOpenState,
    handleAvaliacaoModalOpenState,
    handleDocumentoModalOpenState,
    handleDocumentoDownload,
    handleFornecedorDeleteModalOpenState,
    handleBancoDeleteModalOpenState,
    handleAvaliacaoDeleteModalOpenState,
    handleDocumentoDeleteModalOpenState,
    handlePopperActions,
    handleToggleDesqualificado,
    handleToggleQualificacao,
    handleObservacaoChange,
    handleCepChange,
    handleOpenChangeHistory,
  };
}